import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  LinearProgress,
  Paper,
  Button,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { apiService } from '../services/apiService';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ImageViewerModal from '../components/ImageViewerModal';

const bodyPartOptions = [
  'All',
  'Brazo izquierdo',
  'Brazo derecho',
  'Tórax',
  'Espalda',
  'Pierna izquierda',
  'Pierna derecha',
  'Cuello',
  'Cabeza/Cara',
  'Otra',
  'No especificar',
  '',
];

const HistoryPage: React.FC = () => {
  const [bodyPart, setBodyPart] = useState<string>('All');
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<{
    imageBase64: string;
    segmentationResult?: any;
    preRenderedBase64?: string;
    title: string;
    // Patient details
    age?: number;
    bodyPart?: string;
    diameterLargerThanPencil?: boolean;
    geolocationCity?: string;
    geolocationRegion?: string;
    additionalComment?: string;
    timestamp?: string;
  } | null>(null);

  const queryClient = useQueryClient();

  const { data: analyses, isLoading } = useQuery({
    queryKey: ['analyses', bodyPart],
    queryFn: () => apiService.listAnalyses({
      body_part: bodyPart === 'All' ? undefined : bodyPart,
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (analysisId: string) => apiService.deleteAnalysis(analysisId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analyses'] });
      setDeleteConfirmId(null);
    },
  });

  const handleOpenImageModal = (
    imageBase64: string,
    segmentationResult?: any,
    preRenderedBase64?: string,
    title?: string,
    patientDetails?: {
      age?: number;
      bodyPart?: string;
      diameterLargerThanPencil?: boolean;
      geolocationCity?: string;
      geolocationRegion?: string;
      additionalComment?: string;
      timestamp?: string;
    }
  ) => {
    setSelectedImage({
      imageBase64,
      segmentationResult,
      preRenderedBase64,
      title: title || 'Analysis Image',
      ...patientDetails,
    });
    setImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage(null);
  };

  const getClassificationColor = (classification: string) => {
    switch (classification?.toLowerCase()) {
      case 'benign': return 'success';
      case 'malignant': return 'error';
      case 'other': return 'warning';
      default: return 'default';
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'dd MMM yyyy, HH:mm');
    } catch {
      return dateStr;
    }
  };

  // Calculate metrics
  const totalAnalyses = analyses?.length || 0;
  const highRiskCount = analyses?.filter(a => 
    a.analysis?.advance_classification?.toLowerCase() === 'malignant' && 
    a.analysis?.confidence_level > 0.75
  ).length || 0;
  const riskRate = totalAnalyses > 0 ? (highRiskCount / totalAnalyses * 100).toFixed(1) : '0';
  const uniqueParts = new Set(analyses?.map(a => a.body_part || 'Unknown')).size || 0;

  // Prepare timeline data
  const timelineData = analyses?.map(a => ({
    date: new Date(a.analysis_date).getTime(),
    confidence: a.analysis?.confidence_level || 0,
    risk_level: a.analysis?.advance_classification || 'unknown',
    body_part: a.body_part || 'unknown',
  })) || [];

  const renderAnalysisDetails = (analysis: any) => {
    if (!analysis) return null;

    const confidencePercentage = ((analysis.confidence_level || 0) * 100).toFixed(1);

    return (
      <Box sx={{ mt: 2 }}>
        {/* ABCDE Criteria */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">ABCDE Criteria</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2"><strong>A - Asymmetry:</strong> {analysis.asymmetry || 'N/A'}</Typography>
                <Typography variant="body2"><strong>B - Border Irregularity:</strong> {analysis.border_irregularity || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2"><strong>C - Color Variegation:</strong> {analysis.color_variegation || 'N/A'}</Typography>
                <Typography variant="body2"><strong>D - Diameter Assessment:</strong> {analysis.diameter_assessment || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2"><strong>E - Evolution Assessment:</strong> {analysis.evolution_assessment || 'N/A'}</Typography>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Technical Analysis */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">Technical Analysis</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2"><strong>Segmentation Analysis:</strong> {analysis.segmentation_analysis || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Technical Comments:</strong> {analysis.image_technical_commentaries || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2"><strong>Other Possible Diagnoses:</strong> {analysis.other_diagnoses || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Additional Info Influence:</strong> {analysis.extra_info_influence || 'N/A'}</Typography>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Final Assessment */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle1" gutterBottom>Final Preventive Assessment:</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Chip
                label={analysis.advance_classification || 'Unknown'}
                color={getClassificationColor(analysis.advance_classification)}
              />
            </Grid>
            <Grid item xs>
              <Typography variant="body2">
                Confidence Level: <strong>{confidencePercentage}%</strong>
              </Typography>
              <LinearProgress
                variant="determinate"
                value={parseFloat(confidencePercentage)}
                sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
              />
            </Grid>
          </Grid>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>General Explanation:</strong> {analysis.final_explanation || 'N/A'}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          📋 Preventive Evaluation History
        </Typography>

        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Important:</strong> This history shows preliminary AI-assisted evaluations. 
            They do not constitute medical diagnoses and do not replace regular professional evaluation. 
            However, they can be useful for preventive monitoring of skin lesions and their use will help doctors and users.
          </Typography>
        </Alert>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Body Part</InputLabel>
                <Select
                  value={bodyPart}
                  label="Body Part"
                  onChange={(e) => setBodyPart(e.target.value)}
                >
                  {bodyPartOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option || 'Not specified'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Showing all analyses for selected body part
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {isLoading ? (
          <LoadingSpinner message="Loading history..." />
        ) : !analyses || analyses.length === 0 ? (
          <Alert severity="info">
            No analyses available for this period.
          </Alert>
        ) : (
          <>
            {/* Summary Metrics */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">{totalAnalyses}</Typography>
                    <Typography variant="body2">Total Analyses</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="error">{highRiskCount}</Typography>
                    <Typography variant="body2">High Risk Cases</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning">{riskRate}%</Typography>
                    <Typography variant="body2">Risk Rate</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="info">{uniqueParts}</Typography>
                    <Typography variant="body2">Body Parts</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Timeline Visualization */}
            {timelineData.length > 0 && (
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Temporal Evolution of Analyses
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <ScatterChart data={timelineData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      type="number"
                      scale="time"
                      domain={['dataMin', 'dataMax']}
                      tickFormatter={(value) => format(new Date(value), 'MMM dd')}
                    />
                    <YAxis 
                      dataKey="confidence" 
                      label={{ value: 'Confidence Level', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip 
                      labelFormatter={(value) => format(new Date(value), 'MMM dd, yyyy')}
                      formatter={(value, name) => [
                        name === 'confidence' ? `${(value as number * 100).toFixed(1)}%` : value,
                        name === 'confidence' ? 'Confidence' : name
                      ]}
                    />
                    <Scatter dataKey="confidence" fill="#8884d8" />
                  </ScatterChart>
                </ResponsiveContainer>
              </Paper>
            )}

            {/* Individual Analyses */}
            <Typography variant="h6" gutterBottom>
              Individual Analyses
            </Typography>
            {analyses.map((analysisDoc, index) => (
              <Accordion key={analysisDoc._id || index} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography variant="subtitle1">
                      Analysis from {formatDate(analysisDoc.analysis_date)} - {analysisDoc.body_part || 'Not specified'}
                    </Typography>
                    <Chip
                      label={analysisDoc.analysis?.advance_classification || 'Unknown'}
                      color={getClassificationColor(analysisDoc.analysis?.advance_classification)}
                      size="small"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {renderAnalysisDetails(analysisDoc.analysis)}
                    </Grid>
                  </Grid>

                  {analysisDoc.overall_recommendation && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>General Recommendation:</strong> {analysisDoc.overall_recommendation}
                      </Typography>
                    </Alert>
                  )}

                  {/* Action Buttons */}
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {/* View Image Button */}
                    {analysisDoc.image_b64 && (
                      <Button
                        variant="contained"
                        startIcon={<ImageIcon />}
                        onClick={() => handleOpenImageModal(
                          analysisDoc.image_b64,
                          analysisDoc.segmentation_result,
                          analysisDoc.segmented_image_b64,
                          `Analysis from ${formatDate(analysisDoc.analysis_date)}`,
                          {
                            age: analysisDoc.age,
                            bodyPart: analysisDoc.body_part,
                            diameterLargerThanPencil: analysisDoc.diameter_larger_than_pencil,
                            geolocationCity: analysisDoc.geolocation_city,
                            geolocationRegion: analysisDoc.geolocation_region,
                            additionalComment: analysisDoc.additional_comment,
                            timestamp: analysisDoc.analysis_date,
                          }
                        )}
                        sx={{
                          background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                          color: 'white',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #1976D2 30%, #00B0D7 90%)',
                          },
                        }}
                      >
                        View Image
                      </Button>
                    )}

                    {/* Delete Button */}
                    {deleteConfirmId === analysisDoc._id ? (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography variant="body2" color="error">
                          ¿Confirmar eliminación?
                        </Typography>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => deleteMutation.mutate(analysisDoc._id)}
                          disabled={deleteMutation.isPending}
                        >
                          {deleteMutation.isPending ? 'Eliminando...' : 'Sí, eliminar'}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setDeleteConfirmId(null)}
                          disabled={deleteMutation.isPending}
                        >
                          Cancelar
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => setDeleteConfirmId(analysisDoc._id)}
                      >
                        Eliminar
                      </Button>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </>
        )}

        {/* Image Viewer Modal */}
        {selectedImage && (
          <ImageViewerModal
            open={imageModalOpen}
            onClose={handleCloseImageModal}
            imageBase64={selectedImage.imageBase64}
            segmentationResult={selectedImage.segmentationResult}
            preRenderedBase64={selectedImage.preRenderedBase64}
            title={selectedImage.title}
            age={selectedImage.age}
            bodyPart={selectedImage.bodyPart}
            diameterLargerThanPencil={selectedImage.diameterLargerThanPencil}
            geolocationCity={selectedImage.geolocationCity}
            geolocationRegion={selectedImage.geolocationRegion}
            additionalComment={selectedImage.additionalComment}
            timestamp={selectedImage.timestamp}
          />
        )}
      </Box>
  );
};

export default HistoryPage;
